import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Text,
  Spinner,
  Badge,
  tokens,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import {
  Shield24Regular,
  PlugConnected24Regular,
  Color24Regular,
  PeopleTeam24Regular,
  Copy20Regular,
  Checkmark20Regular,
  Open20Regular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { completeOnboarding } from '../lib/api';
import { logger } from '../lib/logger';

/**
 * First-run onboarding guide for a newly provisioned tenant. Shown as a full-screen modal to the
 * tenant's first user and to any TenantAdmin until the steps are marked complete. Walks the admin
 * through the post-provisioning setup that can't be inferred from data: granting integration admin
 * consent, adding more admins in user management, and optional branding. The on-screen copy avoids
 * Entra ID terminology; only the copied instructions meant for an IT administrator mention it.
 *
 * Provisioning makes the first user a stored TenantAdmin, which is effective in the hybrid mode new
 * tenants start in. In an entraOnly tenant that stored role is inert, so the first user may not be an
 * admin yet ({@link OnboardingGuideProps.isTenantAdmin} false): the guide then leads with assigning
 * the QuickDial.Admin Entra role, which can be copied to the clipboard and handed to a Cloud
 * Application Administrator / Global Administrator. Steps that need admin consent to Microsoft Entra
 * permissions can be copied the same way.
 *
 * The component is presentation only. It calls {@link completeOnboarding} when the admin explicitly
 * finishes, then invokes `onDismiss` so the host can refresh `me` / unmount the guide. "Skip for
 * now" only temporarily dismisses the guide (it will reappear next time QuickDial is opened) unless
 * the user also checks "Do not show again", in which case `onSuppressForever` is called so the host
 * can persist the permanent suppression locally.
 */
export interface OnboardingGuideProps {
  /** Tenant/organization display name, shown in the welcome copy when available. */
  organizationName?: string | null | undefined;
  /** Display name of the current admin, used to personalize the greeting. */
  userName?: string | null | undefined;
  /**
   * Whether the viewer is already an effective tenant admin (`me.tenantRole === 'TenantAdmin'`).
   * When false (the first user of an entraOnly tenant), the guide leads with how to get the
   * QuickDial.Admin Entra role instead of the admin setup steps.
   */
  isTenantAdmin: boolean;
  /**
   * Called after onboarding has been marked complete on the server ("Got it, finish setup").
   * The host should refresh `me` and stop rendering the guide so it never auto-shows again.
   */
  onDismiss: () => void;
  /**
   * Called when the admin clicks "Skip for now" without checking "Do not show again". The host
   * should close the overlay WITHOUT persisting completion — the guide will auto-show again the
   * next time QuickDial is opened.
   */
  onNavigateAway: () => void;
  /**
   * Called when the admin clicks "Skip for now" with "Do not show again" checked.
   * The host should store a permanent suppression flag (e.g. in localStorage) so the guide
   * never auto-shows again without completing onboarding server-side.
   */
  onSuppressForever: () => void;
  /**
   * Called when the admin clicks "Open Integrations" on the consent step. The host should close the
   * overlay WITHOUT persisting completion and navigate to Admin → Integrations. When omitted, the
   * button is not shown.
   */
  onOpenIntegrations?: (() => void) | undefined;
}

interface OnboardingStep {
  key: string;
  icon: FluentIcon;
  title: string;
  /** Renders the step body. Kept as a render fn so steps can include links/lists. */
  body: React.ReactNode;
  /**
   * When set, the step shows a "Copy instructions" button that copies this plain-text version of
   * the step to the clipboard so it can be shared with an IT administrator.
   */
  copyText?: string;
  /** When set, the step shows a primary button that takes the admin straight to where it's done. */
  action?: { label: string; onClick: () => void };
  /** When true, the step is required and rendered with a "Required" badge. */
  required?: boolean;
  /** When true, the step is informational/optional (rendered with a lighter accent). */
  optional?: boolean;
  /**
   * When true, show a hint that the step can only be done after the first administrator has been
   * assigned (steps 2+ live on the Admin page, which needs the QuickDial.Admin role).
   */
  dependsOnFirst?: boolean;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: '640px',
    width: '90vw',
  },
  intro: {
    color: tokens.colorNeutralForeground2,
    display: 'block',
    marginBottom: tokens.spacingVerticalL,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  step: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalM),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  iconWrap: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '2px',
  },
  stepMain: {
    flexGrow: 1,
    minWidth: 0,
  },
  stepTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    marginBottom: tokens.spacingVerticalXS,
  },
  stepBody: {
    color: tokens.colorNeutralForeground2,
    display: 'block',
    marginBottom: tokens.spacingVerticalS,
  },
  bodyNote: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: 0,
  },
  optionalTag: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  dependsHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    display: 'block',
    marginBottom: tokens.spacingVerticalS,
  },
  bodyList: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: 0,
    paddingLeft: tokens.spacingHorizontalL,
    color: tokens.colorNeutralForeground2,
  },
  stepButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  footerButtons: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
});

export function OnboardingGuide({
  organizationName,
  userName,
  isTenantAdmin,
  onDismiss,
  onNavigateAway,
  onSuppressForever,
  onOpenIntegrations,
}: OnboardingGuideProps) {
  const styles = useStyles();
  const [finishing, setFinishing] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copiedResetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current);
      }
    };
  }, []);

  // Copy a step's plain-text instructions so they can be pasted into a message to an IT admin.
  // Uses the modern Clipboard API where available; falls back to execCommand for embedded contexts
  // (e.g. Teams tab) and browsers that do not expose navigator.clipboard.
  const copyInstructions = async (key: string, text: string) => {
    let success = false;

    if (typeof navigator.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch (error) {
        logger.warn('Clipboard API failed, attempting legacy fallback', error, 'ONBOARDING');
      }
    }

    if (!success) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch (error) {
        logger.warn('Legacy copy fallback failed', error, 'ONBOARDING');
      }
    }

    if (success) {
      setCopiedKey(key);
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current);
      }
      copiedResetTimeoutRef.current = window.setTimeout(() => {
        setCopiedKey((k) => (k === key ? null : k));
        copiedResetTimeoutRef.current = null;
      }, 2000);
    } else {
      logger.warn('Failed to copy onboarding instructions to clipboard', undefined, 'ONBOARDING');
    }
  };

  const greeting = userName ? `Welcome, ${userName.split(' ')[0]}!` : 'Welcome to QuickDial!';

  // The first user of a new (hybrid) tenant is provisioned as its stored TenantAdmin, so they can set
  // QuickDial up directly. Only in a tenant whose mode ignores stored roles (entraOnly) can the first
  // user still lack admin rights; for them the guide explains how to get the QuickDial.Admin role.
  const consentInstructions = (
    <ol className={styles.bodyList}>
      <li>
        Open <strong>Admin → Integrations</strong>.
      </li>
      <li>
        Click <strong>Grant Consent</strong> on each integration you want to use.
      </li>
      <li>
        Click <strong>Test Connection</strong> to confirm it works.
      </li>
    </ol>
  );

  const brandingStep: OnboardingStep = {
    key: 'branding',
    icon: Color24Regular,
    title: 'Make it yours',
    optional: true,
    body: (
      <>
        Set your brand colors, custom contact fields and default list columns under{' '}
        <strong>Admin → Branding</strong>.
      </>
    ),
  };

  const inviteStep: OnboardingStep = {
    key: 'invite',
    icon: PeopleTeam24Regular,
    title: 'Invite your team',
    optional: true,
    body: (
      <>
        When the integrations are on, tell your colleagues about QuickDial. Anyone in your organization
        can sign in — they're set up automatically and see the contacts and lists you publish.
      </>
    ),
  };

  const adminSteps: OnboardingStep[] = [
    {
      key: 'consent',
      icon: PlugConnected24Regular,
      title: 'Turn on Teams presence and Outlook sync',
      required: true,
      body: (
        <>
          These features need a one-time approval (<strong>admin consent</strong>) for your
          organization before they work:
          {consentInstructions}
          <p className={styles.bodyNote}>
            <strong>Who can do this:</strong> a <strong>Global Administrator</strong> of your
            Microsoft 365 organization who is also a QuickDial administrator. If that isn't you, first
            make them a QuickDial administrator (see the next step), then use{' '}
            <strong>Copy instructions</strong> and send the steps to them.
          </p>
        </>
      ),
      copyText: [
        'QuickDial — turn on Teams presence and Outlook sync',
        '',
        'This must be done by a Global Administrator of our Microsoft 365 organization.',
        'You have been made a QuickDial administrator so you can open the Admin page.',
        '',
        '  1. Sign in to QuickDial and open Admin → Integrations.',
        '  2. Click "Grant Consent" on each integration (Teams presence, Outlook contact sync).',
        '  3. Click "Test Connection" to confirm it works.',
      ].join('\n'),
      ...(onOpenIntegrations && {
        action: { label: 'Open Integrations', onClick: onOpenIntegrations },
      }),
    },
    {
      key: 'admins',
      icon: Shield24Regular,
      title: 'Add more administrators',
      optional: true,
      body: (
        <>
          To share the work, open <strong>Admin → User management</strong> and add more administrators
          for your organization as needed.
        </>
      ),
    },
    brandingStep,
    inviteStep,
  ];

  // The role assignment itself can only be done outside QuickDial, so the on-screen text stays
  // non-technical and the exact admin-center steps live only in the copied instructions.
  const nonAdminSteps: OnboardingStep[] = [
    {
      key: 'admins',
      icon: Shield24Regular,
      title: 'Get admin permissions',
      required: true,
      body: (
        <>
          You're the first user in your organization, but you don't yet have admin permissions, so you
          can't set up QuickDial yet. Ask your IT administrator (a <strong>Global Administrator</strong>{' '}
          or <strong>Cloud Application Administrator</strong>) to give you the{' '}
          <strong>QuickDial.Admin</strong> role. Use <strong>Copy instructions</strong> to send them
          the exact steps, then sign out and back in once it's done.
        </>
      ),
      copyText: [
        'QuickDial — give a user admin permissions (required)',
        '',
        'This must be done by a Cloud Application Administrator or a Global Administrator.',
        '',
        'Assign the QuickDial.Admin role in Microsoft Entra:',
        '  1. Open the Entra admin center → Enterprise applications:',
        '     https://entra.microsoft.com/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview',
        '  2. Select the "Apps by Exobe" application, then "Users and groups".',
        '  3. Choose "Add user/group" and assign the "QuickDial.Admin" role.',
        '  4. Ask the user to sign out of QuickDial and back in.',
      ].join('\n'),
    },
    {
      key: 'consent',
      icon: PlugConnected24Regular,
      title: 'Turn on Teams presence and Outlook sync',
      dependsOnFirst: true,
      body: (
        <>
          These features need a one-time approval (<strong>admin consent</strong>) for your
          organization from a Global Administrator:
          {consentInstructions}
        </>
      ),
    },
    { ...brandingStep, dependsOnFirst: true },
    { ...inviteStep, dependsOnFirst: true },
  ];

  const steps = isTenantAdmin ? adminSteps : nonAdminSteps;

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await completeOnboarding();
    } catch (error) {
      // Don't trap the admin in the guide if persistence fails — log and dismiss anyway.
      // showOnboarding stays true on the server, so the guide will reappear next time.
      logger.warn('Failed to mark onboarding complete', error, 'ONBOARDING');
    } finally {
      setFinishing(false);
      onDismiss();
    }
  };

  const handleSkip = () => {
    if (doNotShowAgain) {
      onSuppressForever();
    } else {
      onNavigateAway();
    }
  };

  return (
    <Dialog open modalType="modal">
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{greeting}</DialogTitle>
          <DialogContent>
            <Text className={styles.intro}>
              QuickDial is ready for your organization. Start with the <strong>required</strong> step
              below — the rest are optional and can be done any time from the{' '}
              <strong>Admin</strong> page.
            </Text>

            <div className={styles.steps}>
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.key} className={styles.step}>
                    <div className={styles.iconWrap}>
                      <Icon />
                    </div>
                    <div className={styles.stepMain}>
                      <div className={styles.stepTitle}>
                        <Text weight="semibold">{step.title}</Text>
                        {step.required && (
                          <Badge appearance="filled" color="danger" size="small">
                            Required
                          </Badge>
                        )}
                        {step.optional && <span className={styles.optionalTag}>· Optional</span>}
                      </div>
                      {step.dependsOnFirst && (
                        <Text size={200} className={styles.dependsHint}>
                          Do this once you have admin permissions.
                        </Text>
                      )}
                      <div className={styles.stepBody}>
                        {step.body}
                      </div>
                      {(step.action || step.copyText) && (
                        <div className={styles.stepButtons}>
                          {step.action && (
                            <Button
                              size="small"
                              appearance="primary"
                              icon={<Open20Regular />}
                              onClick={step.action.onClick}
                            >
                              {step.action.label}
                            </Button>
                          )}
                          {step.copyText && (
                            <Button
                              size="small"
                              appearance="secondary"
                              icon={copiedKey === step.key ? <Checkmark20Regular /> : <Copy20Regular />}
                              onClick={() => copyInstructions(step.key, step.copyText!)}
                            >
                              {copiedKey === step.key ? 'Copied' : 'Copy instructions'}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions>
            <div className={styles.footerRow}>
              <Checkbox
                label="Do not show again"
                checked={doNotShowAgain}
                onChange={(_, data) => setDoNotShowAgain(!!data.checked)}
              />
              <div className={styles.footerButtons}>
                <Button appearance="subtle" onClick={handleSkip} disabled={finishing}>
                  Skip for now
                </Button>
                <Button appearance="primary" onClick={handleFinish} disabled={finishing}>
                  {finishing ? <Spinner size="tiny" /> : 'Got it, finish setup'}
                </Button>
              </div>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
